import type { Severity } from './types';

/**
 * "Why this matters" business-impact copy (FindingDetail Section 2) — same verified-real-title
 * matching approach as plain-english.ts, reusing the same patterns. Falls back to a
 * severity-based generic sentence (never a fabricated specific claim) when no specific pattern
 * matches, so every finding always has SOME plain-language impact statement.
 */
interface ImpactRule {
  test: RegExp;
  impact: string;
}

const RULES: ImpactRule[] = [
  { test: /^Session-like cookie ".*" missing .+$/, impact: 'An attacker could steal user login sessions from your website visitors.' },
  { test: /^Certificate expiring soon$/, impact: 'Visitors will see security warnings and may lose trust in — or be unable to reach — your site.' },
  { test: /^Deprecated TLS protocol in use/, impact: 'An attacker on the network could intercept or tamper with encrypted traffic to this site.' },
  { test: /^Sensitive-looking path reachable: admin panel$/, impact: 'Anyone on the internet can find and attempt to log into your administrative interface.' },
  {
    test: /^Sensitive-looking path reachable: API schema endpoint$/,
    impact: "An attacker can use this to learn your API's structure and find hidden or undocumented endpoints.",
  },
  {
    test: /^Discovered \d+ reachable route\(s\)$/,
    impact: 'This shows how much of your website is publicly reachable — useful context about your exposure, not a vulnerability by itself.',
  },
  { test: /\(port \d+\) reachable from outside$/, impact: 'An attacker could connect directly to this internal service from the internet.' },
  { test: /^Open port \d+\/\w+/, impact: 'An attacker could connect directly to this internal service from the internet.' },
];

const SEVERITY_FALLBACK: Record<Severity, string> = {
  critical: 'An attacker could use this to seriously compromise your systems or data.',
  high: 'An attacker could use this to compromise your systems or data.',
  medium: 'An attacker could use this to gain a foothold or gather information for further attacks.',
  low: 'This is a minor issue, worth reviewing as part of good security hygiene.',
  info: 'This is informational — not a vulnerability by itself, but useful context about your exposure.',
};

export function getBusinessImpact(title: string, severity: Severity): string {
  const rule = RULES.find((r) => r.test.test(title));
  return rule?.impact ?? SEVERITY_FALLBACK[severity];
}
